/**
 * Lógica pura de ventas y cobranzas. Montos como texto (parseMonto/montoAInput de lib/gastos.ts). El plan de cuotas
 * y la valuación CAC los calcula el backend: acá sólo se arman formularios, payloads, etiquetas y queries.
 */
import type { BadgeTono, OpcionSelect } from "@/components";
import type { OpcionCheckList } from "@/components/CheckList";
import type {
  Cobro, Comprador, CompradorListado, Cuota, Devolucion, EstadoCuota, EstadoUnidad, EstadoVenta, MedioPago, Modalidad, Moneda, ResumenVentas, TipoComprador, TipoCuota, Unidad, UnidadRef, Venta,
} from "./api";
import { periodoDe } from "./cac";
import { formatMoneda, hoyISO } from "./format";
import { ETIQUETA_MEDIO, OPCIONES_MEDIO, TODOS, montoAInput, parseMonto, parseTipoCambio, tipoCambioAInput } from "./gastos";
import { nombreUnidad } from "./unidades";

export { ETIQUETA_MEDIO, OPCIONES_MEDIO, TODOS };

// ------------------------------------------------------------------ etiquetas y opciones

export const ETIQUETA_ESTADO_VENTA: Record<EstadoVenta, string> = { reserva: "Reserva", boleto: "Boleto", escriturada: "Escriturada", desistida: "Desistida" };
export const TONO_ESTADO_VENTA: Record<EstadoVenta, BadgeTono> = { reserva: "neutro", boleto: "primario", escriturada: "exito", desistida: "neutro" };
export const ETIQUETA_ESTADO_CUOTA: Record<EstadoCuota, string> = { pendiente: "Pendiente", parcial: "Parcial", cobrada: "Cobrada", vencida: "Vencida" };
export const TONO_ESTADO_CUOTA: Record<EstadoCuota, BadgeTono> = { pendiente: "neutro", parcial: "primario", cobrada: "exito", vencida: "peligro" };
export const ETIQUETA_ESTADO_UNIDAD: Record<EstadoUnidad, string> = { disponible: "Disponible", reservada: "Reservada", vendida: "Vendida", fuera_de_venta: "Fuera de venta", adjudicada: "Adjudicada" };
export const TONO_ESTADO_UNIDAD: Record<EstadoUnidad, BadgeTono> = { disponible: "exito", reservada: "primario", vendida: "neutro", fuera_de_venta: "alerta", adjudicada: "primario" };

/** El texto del estado: "Adjudicada a Juan Pérez" para las de un clase B; si no, el de la venta. */
export function etiquetaEstado(u: Pick<Unidad, "estado_venta" | "adjudicada_a">): string {
  return u.estado_venta === "adjudicada" && u.adjudicada_a ? `Adjudicada a ${u.adjudicada_a.nombre}` : ETIQUETA_ESTADO_UNIDAD[u.estado_venta];
}
export const ETIQUETA_MODALIDAD: Record<Modalidad, string> = { USD: "USD fijos", ARS_CAC: "Pesos ajustados por CAC" };
export const OPCIONES_MODALIDAD: OpcionSelect[] = [{ valor: "USD", etiqueta: "USD fijos" }, { valor: "ARS_CAC", etiqueta: "Pesos ajustados por CAC" }];
export const ETIQUETA_TIPO_CUOTA: Record<TipoCuota, string> = { anticipo: "Anticipo", cuota: "Cuota", refuerzo: "Refuerzo", posesion: "Posesión" };
export const OPCIONES_TIPO_CUOTA: OpcionSelect[] = (Object.keys(ETIQUETA_TIPO_CUOTA) as TipoCuota[]).map((t) => ({ valor: t, etiqueta: ETIQUETA_TIPO_CUOTA[t] }));
export const ETIQUETA_TIPO_COMPRADOR: Record<TipoComprador, string> = { humana: "Persona", juridica: "Empresa" };
export const OPCIONES_TIPO_COMPRADOR: OpcionSelect[] = (Object.keys(ETIQUETA_TIPO_COMPRADOR) as TipoComprador[]).map((t) => ({ valor: t, etiqueta: ETIQUETA_TIPO_COMPRADOR[t] }));
export const OPCIONES_FILTRO_ESTADO_VENTA: OpcionSelect[] = [
  { valor: TODOS, etiqueta: "Todos los estados" },
  ...(Object.keys(ETIQUETA_ESTADO_VENTA) as EstadoVenta[]).map((e) => ({ valor: e, etiqueta: ETIQUETA_ESTADO_VENTA[e] })),
];

export function nombreUnidades(unidades: UnidadRef[]): string {
  return unidades.length ? unidades.map((u) => nombreUnidad(u)).join(" + ") : "—";
}

export function nombreCuota(c: { numero: number; tipo: TipoCuota }): string {
  return `${ETIQUETA_TIPO_CUOTA[c.tipo]} ${c.numero}`;
}

export function opcionesComprador(lista: CompradorListado[]): OpcionSelect[] {
  return lista.map((c) => ({ valor: String(c.id), etiqueta: c.nombre }));
}

/** Unidades elegibles para una venta: las disponibles, más las que ya están en esta venta (`propias`). */
export function opcionesUnidadesDisponibles(unidades: Unidad[], propias: number[] = []): OpcionCheckList[] {
  return unidades
    .filter((u) => u.estado_venta === "disponible" || propias.includes(u.id))
    .map((u) => ({ valor: String(u.id), etiqueta: nombreUnidad(u), detalle: u.precio_lista_usd ? `Lista ${formatMoneda(u.precio_lista_usd, "USD")}` : undefined }));
}

// ------------------------------------------------------------------ venta

export interface FormVenta {
  comprador_id: string;
  fecha: string;
  modalidad: Modalidad;
  precio: string;
  cac_origen_periodo: string; // "AAAA-MM", sólo si ARS_CAC
  unidad_ids: string[];
  notas: string;
}

export type ErroresVenta = Partial<Record<keyof FormVenta, string>>;

export interface PayloadVenta {
  comprador_id: number;
  fecha: string;
  modalidad: Modalidad;
  precio: string;
  cac_origen_periodo: string | null;
  unidad_ids: number[];
  notas: string | null;
}

export function formVenta(hoy = hoyISO(), v?: Venta & { notas?: string | null }): FormVenta {
  if (!v) return { comprador_id: "", fecha: hoy, modalidad: "USD", precio: "", cac_origen_periodo: periodoDe(hoy), unidad_ids: [], notas: "" };
  return {
    comprador_id: String(v.comprador.id), fecha: v.fecha, modalidad: v.modalidad, precio: montoAInput(v.precio),
    cac_origen_periodo: periodoDe(v.fecha), unidad_ids: v.unidades.map((u) => String(u.id)), notas: v.notas ?? "",
  };
}

export function validarVenta(f: FormVenta): ErroresVenta {
  const e: ErroresVenta = {};
  if (!f.comprador_id) e.comprador_id = "Elegí el comprador";
  if (!f.fecha) e.fecha = "Ingresá la fecha";
  if (parseMonto(f.precio) === null) e.precio = "Ingresá un precio mayor a cero";
  if (f.modalidad === "ARS_CAC" && !/^\d{4}-\d{2}$/.test(f.cac_origen_periodo)) e.cac_origen_periodo = "Elegí el mes de origen";
  if (f.unidad_ids.length === 0) e.unidad_ids = "Elegí al menos una unidad";
  if (f.notas.trim().length > 2000) e.notas = "Hasta 2000 caracteres";
  return e;
}

export function aPayloadVenta(f: FormVenta): PayloadVenta {
  return {
    comprador_id: Number(f.comprador_id), fecha: f.fecha, modalidad: f.modalidad, precio: parseMonto(f.precio) ?? "0",
    cac_origen_periodo: f.modalidad === "ARS_CAC" ? f.cac_origen_periodo : null, unidad_ids: f.unidad_ids.map(Number), notas: f.notas.trim() || null,
  };
}

/** PATCH de la venta: lo mismo sin modalidad ni origen (no se editan). */
export function aPayloadEdicionVenta(f: FormVenta): Omit<PayloadVenta, "modalidad" | "cac_origen_periodo"> {
  const { modalidad: _m, cac_origen_periodo: _p, ...resto } = aPayloadVenta(f);
  return resto;
}

/** PATCH de una venta escriturada o desistida: el backend sólo acepta las notas. */
export function aPayloadNotasVenta(f: FormVenta): { notas: string | null } {
  return { notas: f.notas.trim() || null };
}

/** Monto decimal (texto) mayor a cero, sin pasar por float. */
export function montoPositivo(monto: string | null): boolean {
  return monto !== null && !monto.trim().startsWith("-") && /[1-9]/.test(monto);
}

// ------------------------------------------------------------------ plan (generador)

export interface FormPlan {
  anticipo_monto: string;
  anticipo_fecha: string;
  cuotas_cantidad: string;
  cuotas_monto: string;
  primer_vencimiento: string;
  refuerzos: { monto: string; fecha: string }[];
  posesion_monto: string;
  posesion_fecha: string;
}

export type ErroresPlan = Partial<Record<"general" | "anticipo" | "cuotas" | "refuerzos" | "posesion", string>>;

export interface PayloadParametros {
  anticipo: { monto: string; fecha: string } | null;
  cuotas: { cantidad: number; monto: string; primer_vencimiento: string } | null;
  refuerzos: { monto: string; fecha: string }[];
  posesion: { monto: string; fecha: string } | null;
}

export function formPlan(hoy = hoyISO()): FormPlan {
  return { anticipo_monto: "", anticipo_fecha: hoy, cuotas_cantidad: "", cuotas_monto: "", primer_vencimiento: "", refuerzos: [], posesion_monto: "", posesion_fecha: "" };
}

function hitoCompleto(monto: string, fecha: string): boolean {
  return parseMonto(monto) !== null && fecha !== "";
}

/** El anticipo viene con la fecha precargada, así que cuenta como cargado sólo si tiene monto. */
const conAnticipo = (f: FormPlan): boolean => f.anticipo_monto.trim() !== "";
const conCuotas = (f: FormPlan): boolean => f.cuotas_cantidad !== "" || f.cuotas_monto !== "" || f.primer_vencimiento !== "";
const conPosesion = (f: FormPlan): boolean => f.posesion_monto.trim() !== "" || f.posesion_fecha !== "";

export function validarPlan(f: FormPlan): ErroresPlan {
  const e: ErroresPlan = {};
  if (conAnticipo(f) && !hitoCompleto(f.anticipo_monto, f.anticipo_fecha)) e.anticipo = "Completá monto y fecha del anticipo";
  if (conCuotas(f)) {
    const cantidad = Number(f.cuotas_cantidad);
    if (!Number.isInteger(cantidad) || cantidad < 1 || cantidad > 600 || parseMonto(f.cuotas_monto) === null || !f.primer_vencimiento) e.cuotas = "Completá cantidad, monto y primer vencimiento";
  }
  if (f.refuerzos.some((r) => !hitoCompleto(r.monto, r.fecha))) e.refuerzos = "Completá monto y fecha de cada refuerzo";
  if (conPosesion(f) && !hitoCompleto(f.posesion_monto, f.posesion_fecha)) e.posesion = "Completá monto y fecha de la posesión";
  if (!conAnticipo(f) && !conCuotas(f) && f.refuerzos.length === 0 && !conPosesion(f)) e.general = "Cargá al menos un anticipo, cuotas, un refuerzo o la posesión";
  return e;
}

export function aParametrosPlan(f: FormPlan): PayloadParametros {
  const hito = (monto: string, fecha: string) => ({ monto: parseMonto(monto) ?? "0", fecha });
  return {
    anticipo: conAnticipo(f) ? hito(f.anticipo_monto, f.anticipo_fecha) : null,
    cuotas: conCuotas(f) ? { cantidad: Number(f.cuotas_cantidad), monto: parseMonto(f.cuotas_monto) ?? "0", primer_vencimiento: f.primer_vencimiento } : null,
    refuerzos: f.refuerzos.map((r) => hito(r.monto, r.fecha)),
    posesion: conPosesion(f) ? hito(f.posesion_monto, f.posesion_fecha) : null,
  };
}

// ------------------------------------------------------------------ plan (filas explícitas, edición antes de guardar)

export interface FilaCuota {
  tipo: TipoCuota;
  vencimiento: string;
  monto_base: string;
}

export type ErroresFilas = Partial<Record<"filas", string>>;

export function filaCuota(campos: Partial<FilaCuota> = {}): FilaCuota {
  return { tipo: "cuota", vencimiento: "", monto_base: "", ...campos };
}

/** Las cuotas que devolvió la vista previa, listas para editar en la tabla. */
export function filasDesdeVistaPrevia(cuotas: { tipo: TipoCuota; vencimiento: string; monto_base: string }[]): FilaCuota[] {
  return cuotas.map((c) => ({ tipo: c.tipo, vencimiento: c.vencimiento, monto_base: montoAInput(c.monto_base) }));
}

export function validarFilas(filas: FilaCuota[]): ErroresFilas {
  if (filas.length === 0) return { filas: "Agregá al menos una cuota" };
  if (filas.some((f) => !f.vencimiento || parseMonto(f.monto_base) === null)) return { filas: "Completá vencimiento y monto de cada cuota" };
  return {};
}

export function aPayloadCuotas(filas: FilaCuota[]): FilaCuota[] {
  return filas.map((f) => ({ tipo: f.tipo, vencimiento: f.vencimiento, monto_base: parseMonto(f.monto_base) ?? "0" }));
}

/** Suma de las filas en centavos, para mostrar "suma / precio" mientras se edita. `null` si alguna no parsea. */
export function sumaFilas(filas: FilaCuota[]): string | null {
  let total = 0n;
  for (const f of filas) {
    const m = parseMonto(f.monto_base);
    if (m === null) return null;
    const [ent = "0", dec = ""] = m.split(".");
    total += BigInt(ent) * 100n + BigInt(dec.padEnd(2, "0").slice(0, 2));
  }
  const s = total.toString().padStart(3, "0");
  return `${s.slice(0, -2)}.${s.slice(-2)}`;
}

// ------------------------------------------------------------------ cuota suelta (editar el vencimiento; los montos se cambian rearmando el plan)

export interface FormCuota {
  vencimiento: string;
}

export type ErroresCuota = Partial<Record<keyof FormCuota, string>>;

export function formCuota(c: Cuota): FormCuota {
  return { vencimiento: c.vencimiento };
}

export function validarCuota(f: FormCuota): ErroresCuota {
  const e: ErroresCuota = {};
  if (!f.vencimiento) e.vencimiento = "Ingresá el vencimiento";
  return e;
}

export function aPayloadCuota(f: FormCuota): FormCuota {
  return { vencimiento: f.vencimiento };
}

// ------------------------------------------------------------------ cobros y devoluciones

export interface FormCobro {
  fecha: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string;
  medio: MedioPago;
  nota: string;
  /** id de la caja como texto; "" = la principal (no se manda). */
  caja_id: string;
}

export type ErroresCobro = Partial<Record<"fecha" | "monto" | "tipo_cambio" | "nota", string>>;

export interface PayloadCobro {
  fecha: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  medio: MedioPago;
  nota: string | null;
  caja_id?: number;
}

export function formCobro(c?: Cobro | Devolucion, hoy = hoyISO(), tcSugerido?: string | null, monedaCuenta: Moneda = "USD", cajaDefecto = ""): FormCobro {
  if (!c) return { fecha: hoy, monto: "", moneda: monedaCuenta, tipo_cambio: tipoCambioAInput(tcSugerido), medio: "transferencia", nota: "", caja_id: cajaDefecto };
  return {
    fecha: c.fecha, monto: montoAInput(c.monto), moneda: c.moneda, tipo_cambio: tipoCambioAInput(c.tipo_cambio), medio: c.medio, nota: c.nota ?? "", caja_id: String(c.caja_id),
  };
}

export function necesitaTipoCambio(moneda: Moneda, monedaCuenta: Moneda): boolean {
  return moneda === "ARS" || monedaCuenta === "ARS";
}

export function validarCobro(f: FormCobro, monedaCuenta: Moneda): ErroresCobro {
  const e: ErroresCobro = {};
  if (!f.fecha) e.fecha = "Ingresá la fecha";
  if (parseMonto(f.monto) === null) e.monto = "Ingresá un monto mayor a cero";
  if (necesitaTipoCambio(f.moneda, monedaCuenta) && parseTipoCambio(f.tipo_cambio) === null) e.tipo_cambio = "Ingresá el tipo de cambio";
  if (f.nota.trim().length > 200) e.nota = "Hasta 200 caracteres";
  return e;
}

export function aPayloadCobro(f: FormCobro, monedaCuenta: Moneda): PayloadCobro {
  return {
    fecha: f.fecha, monto: parseMonto(f.monto) ?? "0", moneda: f.moneda,
    tipo_cambio: necesitaTipoCambio(f.moneda, monedaCuenta) ? parseTipoCambio(f.tipo_cambio) : null, medio: f.medio, nota: f.nota.trim() || null,
    ...(f.caja_id ? { caja_id: Number(f.caja_id) } : {}),
  };
}

/** Decimal con hasta `decimales` → entero escalado (ej. "15.5" con 2 → 1550n). */
const escalar = (decimal: string, decimales: number): bigint => {
  const [ent = "0", dec = ""] = decimal.split(".");
  return BigInt(ent) * 10n ** BigInt(decimales) + BigInt(dec.padEnd(decimales, "0").slice(0, decimales));
};

/** num / den redondeado al par más cercano (igual que el ROUND_HALF_EVEN del backend). */
function dividirAlPar(num: bigint, den: bigint): bigint {
  const q = num / den;
  const doble = (num % den) * 2n;
  return doble > den || (doble === den && q % 2n === 1n) ? q + 1n : q;
}

/**
 * Cuánto representa un cobro en la moneda de cuenta de la venta ("Equivale a …" del formulario), en centavos y
 * con redondeo bancario. `monto` y `tipoCambio` son el texto de los inputs. Sólo informativo: el backend recalcula.
 * null si el monto no es válido o hace falta un tipo de cambio que no lo es.
 */
export function equivalenteEnCuenta(monto: string, moneda: Moneda, monedaCuenta: Moneda, tipoCambio: string | null): string | null {
  const m = parseMonto(monto);
  if (m === null) return null;
  let centavos = escalar(m, 2);
  if (moneda !== monedaCuenta) {
    const tc = parseTipoCambio(tipoCambio ?? "");
    if (tc === null) return null;
    const tc4 = escalar(tc, 4);
    centavos = moneda === "USD" ? dividirAlPar(centavos * tc4, 10000n) : dividirAlPar(centavos * 10000n, tc4);
  }
  return `${centavos / 100n}.${String(centavos % 100n).padStart(2, "0")}`;
}

// ------------------------------------------------------------------ transiciones

export interface FormTransicion {
  fecha: string;
  motivo: string;
}

export function formTransicion(hoy = hoyISO()): FormTransicion {
  return { fecha: hoy, motivo: "" };
}

export function validarTransicion(f: FormTransicion, conMotivo: boolean): Partial<Record<keyof FormTransicion, string>> {
  const e: Partial<Record<keyof FormTransicion, string>> = {};
  if (!f.fecha) e.fecha = "Ingresá la fecha";
  if (conMotivo && !f.motivo.trim()) e.motivo = "Indicá el motivo";
  if (f.motivo.trim().length > 200) e.motivo = "Hasta 200 caracteres";
  return e;
}

/** Firmar boleto y escriturar mandan sólo la fecha; desistir, también el motivo. */
export function aPayloadTransicion(f: FormTransicion, conMotivo: boolean): { fecha: string; motivo?: string } {
  return conMotivo ? { fecha: f.fecha, motivo: f.motivo.trim() } : { fecha: f.fecha };
}

// ------------------------------------------------------------------ comprador

export interface FormComprador {
  tipo: TipoComprador;
  nombre: string;
  documento: string;
  email: string;
  telefono: string;
  domicilio: string;
  notas: string;
}

export type ErroresComprador = Partial<Record<keyof FormComprador, string>>;

export function formComprador(c?: Comprador): FormComprador {
  if (!c) return { tipo: "humana", nombre: "", documento: "", email: "", telefono: "", domicilio: "", notas: "" };
  return { tipo: c.tipo, nombre: c.nombre, documento: c.documento ?? "", email: c.email ?? "", telefono: c.telefono ?? "", domicilio: c.domicilio ?? "", notas: c.notas ?? "" };
}

export function validarComprador(f: FormComprador): ErroresComprador {
  const e: ErroresComprador = {};
  if (!f.nombre.trim()) e.nombre = "Ingresá el nombre";
  else if (f.nombre.trim().length > 120) e.nombre = "Hasta 120 caracteres";
  if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Revisá el email";
  if (f.telefono.trim().length > 40) e.telefono = "Hasta 40 caracteres";
  if (f.domicilio.trim().length > 200) e.domicilio = "Hasta 200 caracteres";
  return e;
}

export function aPayloadComprador(f: FormComprador): Record<keyof FormComprador, string | null> {
  const o = (v: string) => v.trim() || null;
  return { tipo: f.tipo, nombre: f.nombre.trim(), documento: o(f.documento), email: o(f.email), telefono: o(f.telefono), domicilio: o(f.domicilio), notas: o(f.notas) };
}

// ------------------------------------------------------------------ filtros y tarjeta

export interface FiltrosVentas {
  estado: string; // TODOS o EstadoVenta
  compradorId: string; // TODOS o id
}

export const filtrosVentasIniciales = (): FiltrosVentas => ({ estado: TODOS, compradorId: TODOS });

export function queryVentas(f: FiltrosVentas): string {
  const partes: string[] = [];
  if (f.estado !== TODOS) partes.push(`estado=${f.estado}`);
  if (f.compradorId !== TODOS) partes.push(`comprador_id=${f.compradorId}`);
  return partes.length ? `?${partes.join("&")}` : "";
}

export interface FiltrosCobros {
  desde: string;
  hasta: string;
  ventaId: string; // TODOS o id
}

export const filtrosCobrosIniciales = (): FiltrosCobros => ({ desde: "", hasta: "", ventaId: TODOS });

export function queryCobros(f: FiltrosCobros): string {
  const partes: string[] = [];
  if (f.desde) partes.push(`desde=${f.desde}`);
  if (f.hasta) partes.push(`hasta=${f.hasta}`);
  if (f.ventaId !== TODOS) partes.push(`venta_id=${f.ventaId}`);
  return partes.length ? `?${partes.join("&")}` : "";
}

/** El Inicio muestra la tarjeta de ventas recién con la primera venta (aunque después se haya desistido). */
export function hayVentas(r: ResumenVentas): boolean {
  return r.activas + r.reservas + r.boletos + r.escrituradas + r.desistidas > 0;
}

export function tarjetaVentas(r: ResumenVentas): { valor: string; detalle: string } {
  const vencidas = r.cuotas_vencidas ? ` · ${r.cuotas_vencidas} cuota${r.cuotas_vencidas === 1 ? "" : "s"} vencida${r.cuotas_vencidas === 1 ? "" : "s"}` : "";
  return { valor: `${r.activas} activa${r.activas === 1 ? "" : "s"}`, detalle: `${formatMoneda(r.cobrado_usd, "USD")} cobrado${vencidas}` };
}
